// Hook que carga el perfil del paciente activo: identidad, kine asignado
// y tratamiento activo. Usado por PerfilPaciente.tsx.
//
// paciente_id resuelto vía useCurrentPacienteId() (string | null).
// Early return si null (loading / lookup-pending / huérfano).
//
// TODO post-feedback: si aparecen pacientes con múltiples tratamientos
// activos simultáneos (ej. hombro + rodilla) que quieran ver TODOS los
// diagnósticos en el perfil, expandir `tratamiento` a `tratamientos[]`.
// Para MVP tomamos solo el más reciente (order by fecha_inicio desc).

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";

export interface ProfilePaciente {
  paciente: {
    nombre: string;
    apellido: string;
    /** ISO date o null si no se registró. */
    fecha_nacimiento: string | null;
  };
  /** El paciente siempre tiene kine (FK NOT NULL en DB), pero null acá si
   *  el join falla por algún motivo inesperado. */
  kine: {
    nombre: string;
  } | null;
  /** Tratamiento activo más reciente. null si el paciente no tiene
   *  ninguno con estado='activo' (ej. después del alta). */
  tratamiento: {
    diagnostico: string | null;
    motivo: string;
    fecha_inicio: string;
  } | null;
}

interface State {
  data: ProfilePaciente | null;
  error: string | null;
  loading: boolean;
}

interface PacienteRowRaw {
  nombre: string;
  apellido: string;
  fecha_nacimiento: string | null;
  kines: { nombre: string } | null;
  tratamientos: Array<{
    diagnostico: string | null;
    motivo: string;
    fecha_inicio: string;
  }>;
}

export function useProfilePaciente(): State {
  const pacienteId = useCurrentPacienteId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    if (!pacienteId) return;
    let cancelled = false;
    (async () => {
      // Sprint 3 fix (5/7/2026): `kines(nombre)` sin `!inner`. Con INNER
      // JOIN + RLS 'kines_select_own' (id = auth.uid()), el paciente no
      // puede leer la fila de su kine → embed devuelve 0 rows → INNER
      // JOIN colapsa la fila del paciente → hook cae en "No encontramos
      // tu perfil" + saludo hardcoded "Paciente" en HomePaciente. El
      // bug afectaba a TODOS los pacientes reales desde que llegó auth
      // real (Sprint 2), pero solo salió a la luz con el swap de ajeroz
      // (5/7). Fix: LEFT JOIN → RLS bloqueando devuelve `kines: null`
      // pero la fila del paciente aparece, hook devuelve nombre real +
      // fallback en el UI. Consumers (PerfilPaciente:102,
      // MensajesPaciente:25) ya manejan `kine?.nombre ?? '—'`.
      // Follow-up separado: migration RLS 'kines_select_by_paciente'
      // para que el nombre del kine aparezca en lugar del fallback.
      const { data, error } = await supabase
        .from("pacientes")
        .select(
          `
          nombre,
          apellido,
          fecha_nacimiento,
          kines(nombre),
          tratamientos(diagnostico, motivo, fecha_inicio, estado)
        `,
        )
        .eq("id", pacienteId)
        .eq("tratamientos.estado", "activo")
        .order("fecha_inicio", {
          foreignTable: "tratamientos",
          ascending: false,
        })
        .maybeSingle()
        .returns<PacienteRowRaw>();

      if (cancelled) return;

      if (error) {
        console.warn("[useProfilePaciente] error cargando perfil", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      if (!data) {
        setState({
          data: null,
          error: "No encontramos tu perfil",
          loading: false,
        });
        return;
      }

      const tratamientoActivo = data.tratamientos[0];
      const profile: ProfilePaciente = {
        paciente: {
          nombre: data.nombre,
          apellido: data.apellido,
          fecha_nacimiento: data.fecha_nacimiento,
        },
        kine: data.kines ? { nombre: data.kines.nombre } : null,
        tratamiento: tratamientoActivo
          ? {
              diagnostico: tratamientoActivo.diagnostico,
              motivo: tratamientoActivo.motivo,
              fecha_inicio: tratamientoActivo.fecha_inicio,
            }
          : null,
      };

      setState({ data: profile, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [pacienteId]);

  return state;
}
